/**
 * Admin → Security page.
 *
 * Two zones:
 *   1. System-wide knobs (max_concurrent_sessions, CSRF kill-switch, MFA policy)
 *   2. This admin's own personal security (reuses SecurityPanel from partner side)
 */
import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Server } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { SecurityPanel } from "@/pages/partner/SecurityPanel";
import AutoDisablePanel from "@/components/AutoDisablePanel";

export default function AdminSecurity() {
    const [s, setS] = useState(null);
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    const reload = () => api.get("/admin/system-settings").then((r) => { setS(r.data); setDraft(r.data); });
    useEffect(() => { reload(); }, []);

    const save = async () => {
        setErr(""); setMsg(""); setBusy(true);
        try {
            const r = await api.patch("/admin/system-settings", draft);
            setS(r.data); setDraft(r.data); setMsg("Saved.");
            setTimeout(() => setMsg(""), 1800);
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    if (!s) return <div className="text-slate-500">Loading…</div>;
    const dirty = JSON.stringify(s) !== JSON.stringify(draft);

    return (
        <div className="space-y-8 max-w-3xl">
            <div>
                <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-blue-600" /> Security
                </h1>
                <p className="text-sm text-slate-500 mt-1">System-wide security policy + your own account controls.</p>
            </div>

            <div className="card p-5 space-y-4">
                <div className="flex items-center gap-2 text-slate-700 font-medium">
                    <Server className="w-4 h-4" /> System settings
                </div>

                <div>
                    <label className="label flex items-baseline justify-between">
                        <span>Max concurrent sessions per user</span>
                        <span className="text-[11px] text-slate-400">1–20</span>
                    </label>
                    <input data-testid="admin-set-max-sessions" type="number" min={1} max={20}
                           value={draft.max_concurrent_sessions}
                           onChange={(e) => setDraft({ ...draft, max_concurrent_sessions: Math.max(1, Math.min(20, parseInt(e.target.value || "1", 10))) })}
                           className="input w-32" />
                    <p className="text-xs text-slate-500 mt-1">
                        When a user logs in beyond this cap, the oldest device is signed out automatically.
                        <strong> 1 = strictest</strong> (only one active device per user).
                    </p>
                </div>

                <div className="flex items-start gap-3 pt-2 border-t border-slate-100">
                    <input data-testid="admin-set-csrf-enabled" id="csrf-toggle" type="checkbox"
                           checked={!!draft.csrf_enabled}
                           onChange={(e) => setDraft({ ...draft, csrf_enabled: e.target.checked })}
                           className="mt-1" />
                    <label htmlFor="csrf-toggle" className="text-sm">
                        <div className="font-medium">CSRF protection</div>
                        <div className="text-xs text-slate-500">Double-submit cookie. Disable ONLY for debugging — leaves the dashboard exposed to cross-site request forgery.</div>
                    </label>
                </div>

                <div className="flex items-start gap-3 pt-2 border-t border-slate-100" data-testid="admin-mfa-enforced-note">
                    <ShieldCheck className="w-4 h-4 mt-0.5 text-emerald-600" />
                    <div className="text-sm">
                        <div className="font-medium">MFA is mandatory for all admin and partner accounts</div>
                        <div className="text-xs text-slate-500">Users without an authenticator are forced to enrol at their next login. MFA cannot be disabled by users.</div>
                    </div>
                </div>

                <div className="flex items-start gap-3 pt-2 border-t border-slate-100">
                    <input data-testid="admin-set-idempotency-required" id="idem-policy" type="checkbox"
                           checked={!!draft.idempotency_required}
                           onChange={(e) => setDraft({ ...draft, idempotency_required: e.target.checked })}
                           className="mt-1" />
                    <label htmlFor="idem-policy" className="text-sm">
                        <div className="font-medium">Require Idempotency-Key on payment APIs</div>
                        <div className="text-xs text-slate-500">When off, the key is honoured whenever sent (24h replay cache) and duplicate client_reference_id is still rejected. When on, payout/payin create calls without the header are rejected with HTTP 400.</div>
                    </label>
                </div>

                {err && <div className="text-rose-600 text-xs">{err}</div>}
                {msg && <div className="text-emerald-600 text-xs">{msg}</div>}

                <div className="flex justify-end">
                    <button data-testid="admin-save-settings" disabled={!dirty || busy} onClick={save}
                            className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2">
                        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save
                    </button>
                </div>
            </div>

            <SecurityPanel />
            <AutoDisablePanel scope="admin" />
        </div>
    );
}
